import {
  faBolt,
  faBullseyeArrow,
  faCrosshairs,
  faCrown,
  faLightbulb,
  faLinkSimple,
  faPencil,
  faTrashCan,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { colours } from '../../assets/colours';
import CircleValueTile from '../../results/components/CircleValueTile';
import { Button } from '../../shared/components/button/Button';
import { Link } from '../../shared/components/link/Link';
import { ContentItem } from '../../shared/components/slider/contentItem/ContentItem';
import WhitePopUpCTA from '../../shared/modal/whitePopUp/WhitePopUpCTA';
import WhitePopUpNoTitle from '../../shared/modal/whitePopUp/WhitePopUpNoTitle';
import { formatAssessmentDate } from '../../shared/util/helperfunctions';

function SingularGoal(props) {
  const { t } = useTranslation();
  const singleGoal = useSelector((state) =>
    state.goals.goals.find((goal) => goal.id === props.goal)
  );
  const goals = useSelector((state) => state.goals.goals);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTextContent, setModalTextContent] = useState('');
  const [openDeleteGoalModal, setOpenDeleteGoalModal] = useState(false);
  const [whitePopUpCTAContents, setWhitePopUpCTAContents] = useState({});
  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, [modalIsOpen]);

  const openModal = useCallback(
    (title, text, helpButtonId) => {
      setModalIsOpen(true);
      setModalTextContent({ title, text });
    },
    [modalIsOpen, modalTextContent]
  );

  const handleDeleteGoal = () => {
    if (goals.length === 1) {
      setWhitePopUpCTAContents({
        title: t('G044'),
        textArr: [
          { text: t('G035'), className: 'sub-heading' },
          {
            text: t('G036'),
            className: '',
          },
        ],
        buttons: [
          {
            text: t('G037'),
            color: 'dark',
            onClick: () => {
              props.handleOpenGoalSelector();
              setOpenDeleteGoalModal(false);
            },
          },
          {
            text: t('G055'),
            color: 'pink',
            onClick: () => {
              setOpenDeleteGoalModal(false);
            },
          },
        ],
      });
    } else {
      setWhitePopUpCTAContents({
        title: t('G044'),
        textArr: [
          { text: t('S020'), className: 'sub-heading' },
          {
            text: t('G054'),
            className: '',
          },
        ],
        buttons: [
          {
            text: t('R013'),
            color: 'dark',
            onClick: () => {
              props.confirmDeleteGoal(singleGoal);
              setOpenDeleteGoalModal(false);
            },
          },
          {
            text: t('G055'),
            color: 'pink',
            onClick: () => {
              setOpenDeleteGoalModal(false);
            },
          },
        ],
      });
    }
    setOpenDeleteGoalModal(true);
  };

  const handleCloseModal = () => {
    setOpenDeleteGoalModal(false);
  };

  return (
    <section
      aria-labelledby={`goal-${singleGoal.id}-title`}
      id={`goal-${singleGoal.id}`}
      className="single-goal-cont "
    >
      {modalIsOpen && (
        <WhitePopUpNoTitle
          returnFocus
          popupContents={modalTextContent}
          ctaBtn={t('T015')}
          handleSubmit={modalClickHandler}
          dialogTitle={t('modal.chart.explanation', { title: modalTextContent.title })}
        />
      )}
      {openDeleteGoalModal && (
        <WhitePopUpCTA
          popupContents={whitePopUpCTAContents}
          dialogTitle={t('modal.goals.remove')}
          onClose={handleCloseModal}
        />
      )}
      <div className="goal-top-line">
        <h2 id={`goal-${singleGoal.id}-title`} className="goal-title fs-exclude">
          <FontAwesomeIcon icon={faBullseyeArrow} style={{ color: colours.orange }} />
          {t(singleGoal.title)}
        </h2>
        <div className="goal-actions ">
          <Button
            className="goal-action"
            onClick={handleDeleteGoal}
            icon={{ faIconDefinition: faTrashCan, size: 'lg' }}
            variant="secondary"
          >
            {t('G044')}
            <span className="sr-only"> {t(singleGoal.title)}</span>
          </Button>
        </div>
      </div>
      <div className="goal-middle-line ">
        <div className="goal-date fs-exclude">
          {t('G045')}
          {formatAssessmentDate(singleGoal.date)}
        </div>
      </div>
      <div className="goal-graph-cont ">
        <CircleValueTile
          id={`${singleGoal.id}-daysOnTarget`}
          title={t('G046')}
          value={t('M070')}
          data={singleGoal.daysOnTarget}
          icon={faBullseyeArrow}
          colour={colours.orange}
          width="third"
          modalText={[t('G038')]}
          openModal={openModal}
          withIcon
          goalTitle={t(singleGoal.title)}
        />
        <CircleValueTile
          id={`${singleGoal.id}-currentStreak`}
          title={t('G047')}
          value={t('M070')}
          data={singleGoal.currentStreak}
          icon={faBolt}
          colour={colours.blue}
          width="third"
          modalText={[t('G039')]}
          openModal={openModal}
          withIcon
          goalTitle={t(singleGoal.title)}
        />
        <CircleValueTile
          id={`${singleGoal.id}-bestStreak`}
          title={t('G048')}
          value={t('M070')}
          data={singleGoal.bestStreak}
          icon={faCrown}
          colour={colours.green}
          width="third"
          modalText={[t('G040')]}
          openModal={openModal}
          withIcon
          goalTitle={t(singleGoal.title)}
        />
        <div className="chart-container goal-half ">
          <div className="title-container">
            <h3 className="title">{t('G049')}</h3>
          </div>
          <div className="chart-rectangle">
            <div className="goal-grey-box">
              <FontAwesomeIcon icon={faCrosshairs} className="icon goals" />
              <div className="goal-grey-box-text">{t('G005')}</div>
              <Link
                icon={{ faIconDefinition: faLinkSimple }}
                color="blue"
                href={`/${singleGoal.url}/results`}
              >
                {t('G052')}
                <span className="sr-only">{t(singleGoal.title)}</span>
              </Link>
            </div>
            <div className="goal-grey-box">
              <FontAwesomeIcon icon={faLightbulb} className="icon goals" />
              <div className="goal-grey-box-text">{t('G007')}</div>
              <Link
                icon={{ faIconDefinition: faLinkSimple }}
                color="blue"
                href={`/${singleGoal.url}/library`}
              >
                {t('G008')}
                <span className="sr-only">{t(singleGoal.title)}</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="chart-container goal-half ">
          <div className="title-container">
            <h3 className="title">{t('G050')}</h3>
            <Link
              icon={{ faIconDefinition: faLinkSimple }}
              color="blue"
              href={`/${singleGoal.url}/library`}
              className="goal-see-more"
            >
              {t('G051')}
              <span className="sr-only">
                {t('goals.recommendations.about')} {t(singleGoal.title)}
              </span>
            </Link>
          </div>
          <div className="media-container goals">
            {singleGoal.media.length > 0 &&
              singleGoal.media.map((item, index) => (
                <ContentItem key={index} slideContent={item} />
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SingularGoal;
